import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload, Users, Palette, CreditCard, Trash2, Plug, Bell } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import Profile from "./Profile";
import UserManagement from "./UserManagement";
import { UsageDashboard } from "@/components/UsageDashboard";
import { TeamInvitations } from "@/components/TeamInvitations";
import { ApiKeysManager } from "@/components/ApiKeysManager";
import { EmailCredentialsManager } from "@/components/EmailCredentialsManager";
import { TeamRoster } from "@/components/TeamRoster";
import { logger } from '@/lib/logger';
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { NotificationPreferences } from "@/components/NotificationPreferences";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  north_logo_url: string | null;
  company_logo_url: string | null;
  executive_summary_prompt: string | null;
  showings_summary_prompt: string | null;
  comps_summary_prompt: string | null;
  email_prompt: string | null;
  neighborhood_summary_prompt: string | null;
  agent_insights_prompt: string | null;
  default_primary_color: string | null;
  default_secondary_color: string | null;
}

interface UserWithRole {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string;
}

const Settings = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadingCompany, setUploadingCompany] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#0c0f24");
  const [secondaryColor, setSecondaryColor] = useState("#1b489b");
  const [savingColors, setSavingColors] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [savingTeamName, setSavingTeamName] = useState(false);
  const { toast } = useToast();

  const activeTab = searchParams.get('tab') || 'profile';

  useEffect(() => {
    fetchProfile();
    checkAdminStatus();
  }, [isImpersonating, impersonatedUser?.id]);

  // Track browser tab visibility to detect tab switches (not navigation)
  // Use sessionStorage to persist across component remounts
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab just became hidden - record timestamp in sessionStorage
        sessionStorage.setItem('settings-tab-hidden-time', Date.now().toString());
      } else {
        // Tab became visible - record timestamp to detect if this is a tab return
        const hiddenTime = sessionStorage.getItem('settings-tab-hidden-time');
        if (hiddenTime) {
          const timeSinceHidden = Date.now() - parseInt(hiddenTime, 10);
          // If tab was hidden within last 5 seconds, mark as tab return
          if (timeSinceHidden < 5000) {
            sessionStorage.setItem('settings-is-tab-return', 'true');
            // Clear the flag after a delay to allow next navigation
            setTimeout(() => {
              sessionStorage.removeItem('settings-is-tab-return');
            }, 1000);
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const checkAdminStatus = async () => {
    const effectiveUserId = await getEffectiveUserId();
    if (!effectiveUserId) {
      logger.error('[Settings] No effective user ID found');
      return;
    }

    logger.debug('[Settings] Checking admin status for user:', effectiveUserId);
    
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', effectiveUserId)
      .eq('role', 'admin')
      .maybeSingle();

    if (error) {
      logger.error('[Settings] Error checking admin status:', error);
    } else {
      logger.debug('[Settings] Admin check result:', data ? 'IS ADMIN' : 'NOT ADMIN', data);
    }

    setIsAdmin(!!data);
    
    if (data) {
      fetchUsers();
    }
  };

  const fetchProfile = async () => {
    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', effectiveUserId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);

      // Fetch team name
      if (data?.team_id) {
        const { data: team } = await supabase
          .from('teams')
          .select('name')
          .eq('id', data.team_id)
          .single();
        if (team) {
          setTeamName(team.name || '');
        }
      }

      // For non-admin users, load the team admin's branding colors
      if (data?.team_id) {
        // First get admin user IDs for this team
        const { data: adminRoles } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'admin');

        if (adminRoles && adminRoles.length > 0) {
          const adminUserIds = adminRoles.map(r => r.user_id);

          // Get the admin user's profile for this team
          const { data: adminProfile } = await supabase
            .from('profiles')
            .select('default_primary_color, default_secondary_color')
            .eq('team_id', data.team_id)
            .in('user_id', adminUserIds)
            .maybeSingle();

          // Use admin's colors for branding
          if (adminProfile) {
            setPrimaryColor(adminProfile.default_primary_color || "#0c0f24");
            setSecondaryColor(adminProfile.default_secondary_color || "#1b489b");
          } else {
            // Fallback to user's own colors
            setPrimaryColor(data.default_primary_color || "#0c0f24");
            setSecondaryColor(data.default_secondary_color || "#1b489b");
          }
        } else {
          // Fallback to user's own colors
          setPrimaryColor(data.default_primary_color || "#0c0f24");
          setSecondaryColor(data.default_secondary_color || "#1b489b");
        }
      } else {
        // Set color states from user's own profile
        if (data) {
          setPrimaryColor(data.default_primary_color || "#0c0f24");
          setSecondaryColor(data.default_secondary_color || "#1b489b");
        }
      }
    } catch (error) {
      logger.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, full_name, email');

      if (profilesError) throw profilesError;

      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      const usersWithRoles: UserWithRole[] = profiles.map(profile => {
        const role = roles.find(r => r.user_id === profile.user_id);

        return {
          id: profile.user_id,
          email: (profile as any).email || 'Unknown',
          full_name: profile.full_name,
          role: role?.role || 'agent',
          created_at: '',
        };
      });

      setUsers(usersWithRoles);
    } catch (error) {
      logger.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to load users.",
        variant: "destructive",
      });
    }
  };

  const handleCompanyLogoUpload = async (file: File) => {
    if (isImpersonating) {
      toast({
        title: "Cannot Edit",
        description: "You cannot edit branding while impersonating a user",
        variant: "destructive",
      });
      return;
    }

    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      setUploadingCompany(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${effectiveUserId}/company-logo-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      // Update only this user's logo
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ company_logo_url: publicUrl })
        .eq('user_id', effectiveUserId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Company logo uploaded successfully",
      });

      fetchProfile();
    } catch (error) {
      logger.error('Error uploading logo:', error);
      toast({
        title: "Error",
        description: "Failed to upload logo",
        variant: "destructive",
      });
    } finally {
      setUploadingCompany(false);
    }
  };

  const handleSaveColors = async () => {
    if (isImpersonating) {
      toast({
        title: "Cannot Edit",
        description: "You cannot edit branding while impersonating a user",
        variant: "destructive",
      });
      return;
    }

    setSavingColors(true);
    try {
      const effectiveUserId = await getEffectiveUserId();
      if (!effectiveUserId) return;

      // Update only this user's colors
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          default_primary_color: primaryColor,
          default_secondary_color: secondaryColor
        })
        .eq('user_id', effectiveUserId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Brand colors saved successfully",
      });

      fetchProfile();
    } catch (error) {
      logger.error('Error saving colors:', error);
      toast({
        title: "Error",
        description: "Failed to save colors",
        variant: "destructive",
      });
    } finally {
      setSavingColors(false);
    }
  };

  const handleSaveTeamName = async () => {
    if (isImpersonating) {
      toast({
        title: "Cannot Edit",
        description: "You cannot edit team settings while impersonating a user",
        variant: "destructive",
      });
      return;
    }

    if (!teamName.trim()) {
      toast({
        title: "Error",
        description: "Team name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setSavingTeamName(true);
    try {
      if (!profile?.team_id) {
        throw new Error("No team found");
      }

      const { error } = await supabase
        .from('teams')
        .update({ name: teamName.trim() })
        .eq('id', profile.team_id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team name updated successfully",
      });
    } catch (error) {
      logger.error('Error saving team name:', error);
      toast({
        title: "Error",
        description: "Failed to save team name",
        variant: "destructive",
      });
    } finally {
      setSavingTeamName(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      const { error: insertError } = await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role: newRole as any }]);

      if (insertError) throw insertError;

      toast({
        title: "Success",
        description: "User role updated",
      });

      fetchUsers();
    } catch (error) {
      logger.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update user role",
        variant: "destructive",
      });
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-6 py-8">
          <p className="text-muted-foreground">Loading...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-6 py-8 max-w-5xl">
        <h1 className="text-3xl font-bold text-foreground mb-6">Settings</h1>

        <Tabs value={activeTab} onValueChange={(value) => navigate(`/settings?tab=${value}`)} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="profile" className="gap-2">
              <Users className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="branding" className="gap-2">
              <Palette className="h-4 w-4" />
              Branding
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2">
              <Plug className="h-4 w-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4" />
              Alerts
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-6 mt-6">
            <Profile />
          </TabsContent>

          <TabsContent value="branding" className="space-y-6 mt-6">
            {isAdmin ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Team Name</CardTitle>
                    <CardDescription>
                      This is your team's display name shown in reports and API responses
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-4">
                      <Input
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="Enter team name"
                        className="max-w-md"
                        disabled={isImpersonating}
                      />
                      <Button
                        onClick={handleSaveTeamName}
                        disabled={savingTeamName || isImpersonating}
                        className="bg-black text-white hover:bg-black/90"
                      >
                        {savingTeamName ? "Saving..." : "Save"}
                      </Button>
                    </div>
                    {isImpersonating && (
                      <p className="text-sm text-muted-foreground">
                        Team name editing is disabled while impersonating
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Company Logo</CardTitle>
                    <CardDescription>
                      This logo will appear on all seller reports you create
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {profile?.company_logo_url && (
                      <div className="space-y-2">
                        <Label>Current Logo</Label>
                        <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/30">
                          <img
                            src={profile.company_logo_url}
                            alt="Company Logo"
                            className="h-16 w-auto object-contain"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (isImpersonating) {
                                toast({
                                  title: "Cannot Edit",
                                  description: "You cannot edit branding while impersonating a user",
                                  variant: "destructive",
                                });
                                return;
                              }

                              const effectiveUserId = await getEffectiveUserId();
                              if (!effectiveUserId) return;

                              // Remove only for this user
                              await supabase
                                .from('profiles')
                                .update({ company_logo_url: null })
                                .eq('user_id', effectiveUserId);

                              toast({
                                title: "Logo removed",
                                description: "Company logo has been removed",
                              });
                              fetchProfile();
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="company-logo">
                        {profile?.company_logo_url ? 'Replace Logo' : 'Upload New Logo'}
                      </Label>
                      <div className="mt-2">
                        <Input
                          id="company-logo"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleCompanyLogoUpload(file);
                          }}
                          disabled={uploadingCompany || isImpersonating}
                        />
                        {isImpersonating && (
                          <p className="text-sm text-muted-foreground mt-2">
                            Logo upload is disabled while impersonating
                          </p>
                        )}
                        {uploadingCompany && (
                          <p className="text-sm text-muted-foreground mt-2">Uploading...</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Default Brand Colors</CardTitle>
                    <CardDescription>
                      These colors will automatically populate new North reports you create
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <Label htmlFor="default-primary-color" className="mb-2 block">
                          Primary Brand Color
                        </Label>
                        <div className="flex items-center gap-4">
                          <div
                            className="w-full h-32 rounded-lg border border-border"
                            style={{ backgroundColor: primaryColor }}
                          />
                          <div className="flex items-center gap-2 bg-background border border-border rounded px-3 py-1.5">
                            <input
                              type="color"
                              id="default-primary-color"
                              value={primaryColor}
                              onChange={(e) => setPrimaryColor(e.target.value)}
                              className="w-0 h-0 invisible"
                              disabled={isImpersonating}
                            />
                            <Label htmlFor="default-primary-color" className="cursor-pointer font-mono text-sm">
                              {primaryColor.toUpperCase()}
                            </Label>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="default-secondary-color" className="mb-2 block">
                          Secondary Brand Color
                        </Label>
                        <div className="flex items-center gap-4">
                          <div
                            className="w-full h-32 rounded-lg border border-border"
                            style={{ backgroundColor: secondaryColor }}
                          />
                          <div className="flex items-center gap-2 bg-background border border-border rounded px-3 py-1.5">
                            <input
                              type="color"
                              id="default-secondary-color"
                              value={secondaryColor}
                              onChange={(e) => setSecondaryColor(e.target.value)}
                              className="w-0 h-0 invisible"
                              disabled={isImpersonating}
                            />
                            <Label htmlFor="default-secondary-color" className="cursor-pointer font-mono text-sm">
                              {secondaryColor.toUpperCase()}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button onClick={handleSaveColors} disabled={savingColors || isImpersonating} className="bg-black text-white hover:bg-black/90">
                      {savingColors ? "Saving..." : "Save Colors"}
                    </Button>
                    {isImpersonating && (
                      <p className="text-sm text-muted-foreground">
                        Brand color editing is disabled while impersonating
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Branding Settings</CardTitle>
                  <CardDescription>
                    Team branding is managed by your administrator
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Palette className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Administrator Access Required</h3>
                    <p className="text-muted-foreground max-w-md">
                      Company branding, including logos and brand colors, are managed by your team administrator.
                      Please contact your admin to make changes to team branding.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="billing" className="space-y-6 mt-6">
            {isAdmin ? (
              <UsageDashboard />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Billing & Usage</CardTitle>
                  <CardDescription>
                    Billing is managed by your team administrator
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CreditCard className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Administrator Access Required</h3>
                    <p className="text-muted-foreground max-w-md">
                      Billing, subscription plans, and usage limits are managed by your team administrator.
                      Please contact your admin for billing-related questions or to upgrade your plan.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6 mt-6">
            <EmailCredentialsManager isAdmin={isAdmin} />
            {isAdmin ? (
              <ApiKeysManager />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>API Integrations</CardTitle>
                  <CardDescription>
                    API keys are managed by your team administrator
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Plug className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Administrator Access Required</h3>
                    <p className="text-muted-foreground max-w-md">
                      API keys and external integrations are managed by your team administrator.
                      Please contact your admin to set up integrations with Zapier, Make.com, or other services.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="notifications" className="space-y-6 mt-6">
            <NotificationPreferences />
          </TabsContent>

          <TabsContent value="users" className="space-y-6 mt-6">
            {isAdmin ? (
              <>
                <TeamInvitations />
                <TeamRoster />
                <UserManagement />
              </>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>
                    User management is handled by your team administrator
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Users className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Administrator Access Required</h3>
                    <p className="text-muted-foreground max-w-md">
                      Team member management, including inviting new users and managing roles, is handled by your team administrator.
                      Please contact your admin to invite new team members or modify user permissions.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Settings;
